import {
  createContext,
  useContext,
  type HTMLAttributes,
  type ReactNode,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from './cn.js';

/**
 * Data table.
 *
 * Composable rather than config-driven, because every real table ends up
 * needing one cell that the config could not express. What the component owns
 * is the stuff that is easy to get subtly wrong and makes a table look cheap:
 *
 *   - a sticky header that stays legible over scrolling content
 *   - one hairline between rows, at low contrast, and none after the last
 *   - right-aligned tabular numerics
 *   - a hover state that covers the full row including sticky columns
 *   - a density switch, since 40 rows and 4 rows want different rhythm
 */

type Density = 'compact' | 'normal' | 'relaxed';

const DensityCtx = createContext<Density>('normal');

const ROW_PAD: Record<Density, string> = {
  compact: 'px-3 py-1.5',
  normal: 'px-3 py-2.5',
  relaxed: 'px-4 py-3.5',
};

export function Table({
  children,
  density = 'normal',
  minWidth,
  className,
  containerClassName,
}: {
  children: ReactNode;
  density?: Density;
  /** Below this the container scrolls sideways rather than the page. */
  minWidth?: number | string;
  className?: string;
  containerClassName?: string;
}) {
  return (
    <DensityCtx.Provider value={density}>
      <div className={cn('scroll-x relative w-full', containerClassName)}>
        <table
          className={cn('w-full border-collapse text-left align-middle', className)}
          style={minWidth ? { minWidth } : undefined}
        >
          {children}
        </table>
      </div>
    </DensityCtx.Provider>
  );
}

export function THead({ children, sticky = true }: { children: ReactNode; sticky?: boolean }) {
  return (
    <thead
      className={cn(
        'text-2xs uppercase tracking-wide text-ink-tertiary',
        // The blurred translucent header is what keeps a long table feeling
        // like one surface while it scrolls under the labels.
        sticky && 'sticky top-0 z-10 glass',
      )}
    >
      {children}
    </thead>
  );
}

export function TBody({ children }: { children: ReactNode }) {
  return (
    <tbody className="[&>tr:not(:last-child)]:border-b [&>tr]:border-line-subtle">{children}</tbody>
  );
}

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Adds a hover surface and a pointer, for rows that navigate. */
  interactive?: boolean;
  selected?: boolean;
}

export function TR({ interactive, selected, className, children, ...rest }: TRProps) {
  return (
    <tr
      className={cn(
        'transition-colors duration-fast',
        interactive && 'cursor-pointer hover:bg-surface-hover',
        selected && 'bg-brand-tint',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export type SortDirection = 'asc' | 'desc' | null;

export interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
  /** Renders a sort affordance and makes the header a button. */
  sortable?: boolean;
  direction?: SortDirection;
  onSort?: () => void;
  width?: number | string;
}

export function TH({
  numeric,
  sortable,
  direction = null,
  onSort,
  width,
  className,
  children,
  ...rest
}: THProps) {
  const density = useContext(DensityCtx);
  const Icon = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ChevronsUpDown;

  return (
    <th
      scope="col"
      aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : undefined}
      style={width ? { width } : undefined}
      className={cn(
        'border-b border-line bg-transparent font-semibold',
        density === 'compact' ? 'px-3 py-2' : 'px-3 py-2.5',
        numeric && 'text-right',
        className,
      )}
      {...rest}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            'group -mx-1 inline-flex items-center gap-1 rounded-xs px-1 py-0.5 uppercase',
            'transition-colors hover:text-ink',
            numeric && 'flex-row-reverse',
          )}
        >
          {children}
          <Icon
            aria-hidden
            className={cn(
              'h-3 w-3 shrink-0 transition-opacity',
              direction ? 'opacity-100' : 'opacity-0 group-hover:opacity-50',
            )}
          />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
  /** Muted, for supporting columns. */
  muted?: boolean;
}

export function TD({ numeric, muted, className, children, ...rest }: TDProps) {
  const density = useContext(DensityCtx);
  return (
    <td
      className={cn(
        ROW_PAD[density],
        numeric && 'nums text-right',
        muted && 'text-ink-secondary',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

/** A stack of a primary line and a quieter secondary line inside one cell. */
export function CellStack({
  primary,
  secondary,
  className,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="truncate text-sm text-ink">{primary}</div>
      {secondary && (
        <div className="mt-0.5 flex items-center gap-1.5 truncate text-2xs text-ink-tertiary">
          {secondary}
        </div>
      )}
    </div>
  );
}
